// The assistant service over HTTP: one POST per turn, answered as server-sent events. The scoped token comes from
// ai_open_session(); the person's own session token never goes to the service.
import { useAuth } from './auth';
import { ApiError, type Json } from './api';
import { supabaseUrl } from './supabase';

export function assistantBase(): string {
  const env = import.meta.env.VITE_ASSISTANT_URL as string | undefined;
  if (env) return env.replace(/\/$/, '');
  if (import.meta.env.DEV) { try { const u = new URL(supabaseUrl); return `${u.protocol}//${u.hostname}:8787/assistant/v1`; } catch { /* fall through */ } }
  return `${supabaseUrl.replace(/\/$/, '')}/assistant/v1`;
}

export type TurnEvent =
  | { type: 'status'; text: string }
  | { type: 'text'; delta: string }
  | { type: 'matches'; strong: boolean; candidates: Json[] }
  | { type: 'proposal'; id: string; status: string }
  | { type: 'escalated'; model: string; reason: string }
  | { type: 'done'; message_id?: number; cost_inr: number; model: string; tool_rounds: number; template?: boolean }
  | { type: 'error'; code: string; detail?: string; message_id?: number };

export async function streamTurn(opts: { token: string; text: string; files: { id: string; blob: Blob; name: string }[]; attachmentIds: string[]; thinkHarder?: boolean; onEvent: (e: TurnEvent) => void | Promise<void>; signal?: AbortSignal }): Promise<void> {
  const fd = new FormData();
  fd.set('text', opts.text);
  fd.set('attachment_ids', JSON.stringify(opts.attachmentIds));
  if (opts.thinkHarder) fd.set('think_harder', '1');
  for (const f of opts.files) fd.set(`file:${f.id}`, f.blob, f.name);
  let r: Response;
  try { r = await fetch(`${assistantBase()}/turn`, { method: 'POST', headers: { Authorization: `Bearer ${opts.token}` }, body: fd, signal: opts.signal }); }
  catch (e) { if ((e as Error).name === 'AbortError') return; throw new ApiError('provider_unavailable', 'the assistant service is not reachable'); }
  if (!r.ok || !r.body) { let code = `http_${r.status}`; try { code = (await r.json()).error ?? code; } catch { /* no body */ } throw new ApiError(code === 'http_502' || code === 'http_503' ? 'provider_unavailable' : code); }
  const reader = r.body.getReader(); const dec = new TextDecoder(); let buf = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let i: number;
      while ((i = buf.indexOf('\n\n')) >= 0) {
        const chunk = buf.slice(0, i); buf = buf.slice(i + 2);
        const data = chunk.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trim()).join('\n');
        if (!data) continue;
        try { await opts.onEvent(JSON.parse(data) as TurnEvent); } catch { /* a malformed event is skipped */ }
      }
    }
  } catch (e) {
    // stopped by the person: the service records the stop and the conversation shows it
    if (opts.signal?.aborted || (e as Error).name === 'AbortError') return;
    throw new ApiError('provider_unavailable', 'the connection to the assistant service was lost');
  }
}

export async function assistantHealth(): Promise<Json | null> {
  try { const r = await fetch(`${assistantBase()}/health`, { signal: AbortSignal.timeout(5000) }); return r.ok ? ((await r.json()) as Json) : { ok: false, status: r.status }; } catch { return null; }
}

/** Whether the signed-in person sees the assistant: the kill switch and ai.allowed_roles. */
export function useAssistantEnabled(): boolean {
  const { settings, roles } = useAuth();
  if (settings['ai.enabled'] !== true && settings['ai.enabled'] !== 'true') return false;
  const allowed = (Array.isArray(settings['ai.allowed_roles']) ? settings['ai.allowed_roles'] : ['admin', 'accountant', 'approver']) as string[];
  return roles.includes('admin') || roles.some((r) => allowed.includes(r));
}

/** The record a page is about, for the assistant's context. */
export function pageContext(pathname: string): Json | null {
  const m = pathname.match(/^\/(expenses|payments|bills|sales|bank|parties)\/([0-9a-f-]{36})/);
  if (!m) return null;
  const entity = { expenses: 'expense', payments: 'payment', bills: 'bill', sales: 'sales_invoice', bank: 'bank_statement', parties: 'party' }[m[1]];
  return { entity, id: m[2] };
}
